import { URL_ACCOUNT_BRANDING } from '@seek/adv-constants';
import { useTranslations } from '@vocab/react';
import {
  Actions,
  Box,
  Button,
  Column,
  Columns,
  Heading,
  Stack,
  Text,
} from 'braid-design-system';
import React from 'react';
import { useSelector, shallowEqual } from 'react-redux';

import { EVENT_NAME_INTERACTION } from 'src/app/analytics';
import getTrackingAdapter from 'src/app/dataAnalytics';
import { ADD_COVER_IMAGE_CTA_PRESSED } from 'src/app/dataAnalytics/schema/constants';
import {
  FEATURE_DATA_ANALYTICS,
  useFeatureToggleData,
} from 'src/app/featureToggle';
import { sendLinkEvent } from 'src/app/utils/analytics';
import { useLocalisedRoutes } from 'src/app/views/hooks/useLocalisedRoutes';
import type AppState from 'src/types/appState';

import { PanelImage } from '../PanelImage';

import translations from './.vocab';
import showcaseImage from './showcaseImage.jpg';

export const ShowcaseYourBrandPanel = () => {
  const { t } = useTranslations(translations);
  const isPageAnalyticsEnabled = useFeatureToggleData(FEATURE_DATA_ANALYTICS);
  const { localiseRoute } = useLocalisedRoutes();

  const { brandId, tealium } = useSelector(
    (state: AppState) => ({
      brandId: state.formData.brand?.id,
      tealium: state.tealium,
    }),
    shallowEqual,
  );

  const handleOnClick = () => {
    if (isPageAnalyticsEnabled) {
      const eventData = {
        objectInteraction: 'ecaja-add-cover-image',
        eventName: ADD_COVER_IMAGE_CTA_PRESSED,
      };
      getTrackingAdapter.sendLinkEvent(eventData);
    } else {
      const ANALYTICS_LINK_NAME = 'ecaja-add-cover-image-cta';
      const eventData = {
        linkName: ANALYTICS_LINK_NAME,
        eventName: [EVENT_NAME_INTERACTION, ANALYTICS_LINK_NAME],
        objectInteraction: 'ecaja-add-cover-image',
      };
      sendLinkEvent(tealium, eventData);
    }

    const brandingPageRedirectUrl = brandId
      ? `${URL_ACCOUNT_BRANDING}/brands/${brandId}?redirectUrl=/dashboard`
      : `${URL_ACCOUNT_BRANDING}`;

    const localisedBrandingPageRedirectUrl = localiseRoute(
      brandingPageRedirectUrl,
    );

    window.open(localisedBrandingPageRedirectUrl);
  };

  return (
    <Stack space="small">
      <Box background="surface">
        <Columns space="none" collapseBelow="tablet">
          <Column>
            <PanelImage imageUrl={showcaseImage} />
          </Column>
          <Column>
            <Box
              padding="large"
              alignItems="center"
              display="flex"
              height="full"
            >
              <Stack space="medium">
                <Heading level="3">{t('showcase-brand-heading')}</Heading>
                <Text tone="secondary">{t('showcase-brand-text')}</Text>
                <Box marginTop="xlarge">
                  <Actions>
                    <Button onClick={handleOnClick} variant="ghost">
                      {t('showcase-brand-button-label')}
                    </Button>
                  </Actions>
                </Box>
              </Stack>
            </Box>
          </Column>
        </Columns>
      </Box>
    </Stack>
  );
};
